'use client';
import { PaginationButton } from '@/blocks/components/Controls/Pagination/PaginationButton';
import { PaginationPrevNextButton } from '@/blocks/components/Controls/Pagination/PaginationPrevNextButton';
import { WithClassName } from '@/blocks/components/types';
import usePagination, { UsePaginationItem } from '@mui/material/usePagination/usePagination';
import classNames from 'classnames';
import { memo, useEffect, useRef, useState } from 'react';
import styles from './styles.module.scss';

type PaginationControllerType = {
  reset: () => void;
};

export type PaginationController = React.MutableRefObject<PaginationControllerType>;

export type PaginationProps = {
  count: number;
  perPage: number;
  onChange: (page: number) => void;
  controller?: PaginationController;
} & WithClassName;

export function usePaginationController() {
  const ref = useRef<PaginationControllerType>({
    reset() {},
  });
  return { paginationController: ref };
}

function getPaginationComponent(
  componentProps: Pick<UsePaginationItem, 'onClick' | 'type' | 'selected' | 'disabled'>,
  page: UsePaginationItem['page'],
) {
  let children = null;
  if (componentProps.type === 'previous' || componentProps.type === 'next') {
    children = <PaginationPrevNextButton {...componentProps} />;
  } else {
    children = <PaginationButton {...componentProps}>{page || '...'}</PaginationButton>;
  }
  return children;
}

export const Pagination = memo(function Pagination({
  className,
  count,
  perPage,
  onChange,
  controller,
}: PaginationProps) {
  const [selectedPage, setSelectedPage] = useState(1);
  const totalPages = Math.ceil(count / perPage);
  const { items } = usePagination({
    count: totalPages,
    onChange: (_, number) => {
      setSelectedPage(number);
      onChange(number);
    },
    page: selectedPage,
  });

  useEffect(() => {
    if (selectedPage > totalPages && totalPages !== 0) {
      onChange(totalPages);
      setSelectedPage(totalPages);
    }
  }, [onChange, selectedPage, totalPages]);

  if (controller) {
    controller.current.reset = function () {
      setSelectedPage(1);
      onChange(1);
    };
  }

  if (totalPages <= 1) {
    return <div className={styles.paginationSpaceTopStyles}></div>;
  }

  return (
    <ul className={classNames(styles.pagination, className)}>
      {items.map(({ page, ...otherProps }, index) => {
        return <li key={index}>{getPaginationComponent(otherProps, page)}</li>;
      })}
    </ul>
  );
});

// @ts-ignore
export { paginationSpaceTopStyles } from './styles.module.scss';
